import { backendClient, getAccessToken } from "@/api/backendClient";

import type {
  CaseTemplateToPublicPortal,
  PortalAccessBody,
  PortalCase,
  PortalCaseCreated,
  PortalCaseInsert,
  PortalCaseTemplate,
  PortalDetails,
  PortalDevice,
  PortalDevicesBodyInput,
  PortalLogo,
  PortalTarget,
  PublicPortal,
  PublicPortalExtended,
  PublicPortalInsert,
  PublicPortalUpdate,
} from "@waku-care/types/contracts/internal";

// * Public Portal management (authenticated)

export type CaseTemplateLink = Pick<CaseTemplateToPublicPortal, "public_portal_id" | "case_template_id">;

export const getPublicPortals = async () => {
  const { data, error } = await backendClient.get<PublicPortalExtended[]>("/public-portals");
  if (error) throw error;
  return data;
};

// a deployment has at most one public portal
export const getPublicPortalByDeploymentId = async (deployment_id: string) => {
  const { data, error } = await backendClient.get<PublicPortalExtended[]>("/public-portals", {
    params: { deployment_id },
  });
  if (error) throw error;
  return data[0] ?? null;
};

export const createPublicPortal = async ({ values }: { values: PublicPortalInsert }) => {
  const { data, error } = await backendClient.post<PublicPortal>("/public-portals", { body: values });
  if (error) throw error;
  return data;
};

export const updatePublicPortal = async ({ id, values }: { id: string; values: PublicPortalUpdate }) => {
  const { data, error } = await backendClient.patch<PublicPortal>(`/public-portals/${id}`, { body: values });
  if (error) throw error;
  return data;
};

// linking is idempotent on the backend, so a link that already exists is no error
export const linkPublicPortalCaseTemplate = async ({ public_portal_id, case_template_id }: CaseTemplateLink) => {
  const { data, error } = await backendClient.put<CaseTemplateToPublicPortal>(
    `/public-portals/${public_portal_id}/case-templates/${case_template_id}`,
  );
  if (error) throw error;
  return data;
};

export const unlinkPublicPortalCaseTemplate = async ({ public_portal_id, case_template_id }: CaseTemplateLink) => {
  const { error } = await backendClient.delete<null>(
    `/public-portals/${public_portal_id}/case-templates/${case_template_id}`,
  );
  if (error) throw error;
};

// * Public Portal (unauthenticated, via portal token & pin)

// an invalid pin/token resolves to `null`, not an error, so the client can probe whether a portal has a pin
export const getPublicPortalTarget = async (body: PortalAccessBody) => {
  const { data, error } = await backendClient.post<PortalTarget | null>("/portal/target", {
    authenticated: false,
    body,
  });
  if (error) throw error;
  return data;
};

export const getPublicPortalDetails = async (body: PortalAccessBody) => {
  const { data, error } = await backendClient.post<PortalDetails>("/portal/details", { authenticated: false, body });
  if (error) throw error;
  return data;
};

export const getPublicPortalDevices = async (body: PortalDevicesBodyInput) => {
  const { data, count, error } = await backendClient.post<PortalDevice[]>("/portal/devices", {
    authenticated: false,
    body,
  });
  if (error) throw error;
  return { data, count: count ?? 0 };
};

export const getPublicPortalCaseTemplates = async (body: PortalAccessBody) => {
  const { data, error } = await backendClient.post<PortalCaseTemplate[]>("/portal/case-templates", {
    authenticated: false,
    body,
  });
  if (error) throw error;
  return data;
};

export const getPublicPortalCases = async (body: PortalAccessBody) => {
  const { data, error } = await backendClient.post<PortalCase[]>("/portal/cases", { authenticated: false, body });
  if (error) throw error;
  return data;
};

// a logged in reporter sends their access token, so they are recorded as case creator. it's optional,
// the portal is public, so this is an unauthenticated route with the token attached when available
export const createPublicPortalCase = async (body: PortalCaseInsert) => {
  const accessToken = await getAccessToken().catch(() => null);
  const { data, error } = await backendClient.post<PortalCaseCreated>("/portal/cases/create", {
    authenticated: false,
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
    body,
  });
  if (error) throw error;
  return data.id;
};

// the workspace profile logo lives in a private bucket that anon cannot read, so the
// signed url is minted by the backend holding the service role
export const getPublicPortalLogo = async (body: PortalAccessBody): Promise<PortalLogo> => {
  const { data, error } = await backendClient.post<PortalLogo>("/portal/logo", { authenticated: false, body });
  if (error) throw error;
  return data ?? { url: null };
};

export const uploadPublicPortalFiles = async (formData: FormData) => {
  const { error } = await backendClient.post<null>("/portal/files", { authenticated: false, body: formData });
  if (error) throw error;
};
